if (!customElements.get('sticky-atc-panel')) {
  class StickyAtcPanel extends HTMLElement {
    constructor() {
      super();

      this.productSection = this.closest('.cc-main-product');
      this.productInfo = this.productSection.querySelector('.product-info');
      this.productForm = this.productSection.querySelector('product-form');

      this.atcBtn = this.querySelector('.js-sticky-atc');
      this.stickyQty = this.querySelector('.js-sticky-qty');
      this.stickyInventory = this.querySelector('.js-sticky-inventory');
      this.productUrl = this.dataset.productUrl;
      this.variantsAnchor = this.dataset.variantsAnchor || 'product-info';
      this.quickAddEnabled = this.dataset.quickAddEnabled === 'true';
      this.singleVariant = this.dataset.singleVariant === 'true';
      this.mode = 'add'; // 'add' | 'drawer' | 'disabled'

      if ('IntersectionObserver' in window && 'MutationObserver' in window) {
        this.bindEvents();
      }

      this.bindAtc();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', StickyAtcPanel.handleScroll);
      if (this.inventoryObserver) this.inventoryObserver.disconnect();
    }

    bindEvents() {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target === this.productForm && !theme.mediaMatches.md) {
            this.classList.toggle('sticky-atc-panel--out', entry.boundingClientRect.bottom > 0);
          } else if (entry.target === this.productInfo && theme.mediaMatches.md) {
            this.classList.toggle('sticky-atc-panel--out', entry.isIntersecting);
          }
        });
      });

      if (this.productForm) observer.observe(this.productForm);
      if (this.productInfo) observer.observe(this.productInfo);

      window.addEventListener('scroll', StickyAtcPanel.handleScroll);
    }

    bindAtc() {
      if (!this.atcBtn) return;

      // Délégué sur la section : survit au remplacement des nœuds par le section rendering.
      this.productSection.addEventListener('on:variant:change', () => {
        this.syncState();
        this.syncInventory();
        this.syncQtyFromMain();
      });

      // Animation du bouton (miroir de product-form.js) : succès -> check, erreur -> reset.
      const onAddSuccess = () => {
        if (!this.atcBtn.classList.contains('is-loading')) return;
        this.atcBtn.classList.add('is-success');
        this.atcBtn.removeAttribute('aria-disabled');
        clearTimeout(this.successTimer);
        this.successTimer = setTimeout(() => {
          this.atcBtn.classList.remove('is-loading', 'is-success');
        }, 1400);
      };
      this.productSection.addEventListener('on:cart:add', onAddSuccess);
      this.productSection.addEventListener('on:line-item:change', onAddSuccess);
      this.productSection.addEventListener('on:cart:error', () => {
        this.atcBtn.classList.remove('is-loading');
        this.atcBtn.removeAttribute('aria-disabled');
      });

      // Quantité : liaison bidirectionnelle (délégation -> survit au re-render du conteneur principal).
      this.productSection.addEventListener('change', (evt) => {
        if (evt.target === this.stickyQty) {
          const mainQty = this.productSection.querySelector('.js-product-form-main [name="quantity"]');
          if (mainQty && mainQty.value !== this.stickyQty.value) {
            mainQty.value = this.stickyQty.value;
            mainQty.dispatchEvent(new Event('change', { bubbles: true }));
          }
        } else if (this.stickyQty && evt.target.matches
          && evt.target.matches('.js-product-form-main [name="quantity"]')) {
          if (this.stickyQty.value !== evt.target.value) {
            this.stickyQty.value = evt.target.value;
          }
        }
      });

      this.atcBtn.addEventListener('click', (evt) => {
        if (this.atcBtn.getAttribute('aria-disabled') === 'true') {
          evt.preventDefault();
          return;
        }
        if (this.mode === 'add') {
          evt.preventDefault();
          // Un ajout principal déjà en cours -> product-form ignorerait la soumission (sans émettre
          // d'événement), ce qui figerait l'animation. On n'amorce pas dans ce cas.
          const mainBtn = this.productSection.querySelector('.js-product-form-main [name="add"]');
          if (mainBtn && mainBtn.getAttribute('aria-disabled') === 'true') return;
          this.atcBtn.classList.add('is-loading');
          this.atcBtn.setAttribute('aria-disabled', 'true');
          this.addToCart();
        } else if (this.mode === 'drawer') {
          evt.preventDefault();
          this.openChooser();
        }
        // mode 'disabled' : bouton disabled, pas de clic.
      });

      this.syncState();
      this.syncInventory();
      this.syncQtyFromMain();
    }

    /**
     * Définit le mode du bouton sticky :
     *  - produit multi-variantes -> 'drawer' (« Choisir une option » ouvre le tiroir pour choisir + ajouter) ;
     *  - mono-variante indisponible (bouton add principal disabled) -> 'disabled' (épuisé) ;
     *  - mono-variante disponible -> 'add' (ajout direct).
     */
    syncState() {
      const mainForm = this.productSection.querySelector('.js-product-form-main');
      const mainBtn = mainForm && mainForm.querySelector('[name="add"]');
      if (!mainForm || !mainBtn) return;

      if (!this.singleVariant) {
        // Multi-variantes : le bouton ouvre toujours le tiroir pour choisir la variante.
        this.mode = 'drawer';
        this.atcBtn.disabled = false;
        this.atcBtn.removeAttribute('aria-disabled');
        this.atcBtn.textContent = this.dataset.chooseText;
        this.atcBtn.setAttribute('aria-label', this.dataset.chooseText);
        return;
      }

      // Mono-variante.
      if (mainBtn.disabled) {
        this.mode = 'disabled';
        this.atcBtn.disabled = true;
        this.atcBtn.setAttribute('aria-disabled', 'true');
        this.atcBtn.textContent = mainBtn.textContent.trim();
        this.atcBtn.setAttribute('aria-label', mainBtn.textContent.trim());
      } else {
        this.mode = 'add';
        this.atcBtn.disabled = false;
        this.atcBtn.removeAttribute('aria-disabled');
        this.atcBtn.textContent = this.dataset.addText;
        this.atcBtn.setAttribute('aria-label', this.dataset.addText);
      }
    }

    /**
     * Mode 'drawer' : ouvre le tiroir quick-add du produit courant si le composant est disponible,
     * sinon défile vers le sélecteur de variantes (repli).
     */
    openChooser() {
      const drawer = document.querySelector('quick-add-drawer');
      if (this.quickAddEnabled && drawer && typeof drawer.open === 'function') {
        this.atcBtn.dataset.productUrl = this.productUrl;
        drawer.open(this.atcBtn);
        return;
      }
      const anchor = document.getElementById(this.variantsAnchor);
      if (anchor) {
        anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        window.location.hash = this.variantsAnchor;
      }
    }

    /**
     * Reflète le product-inventory principal (statut + urgence + couleur de pastille).
     * Un MutationObserver capte le remplissage lazy (product-inventory.js), les changements de variante
     * et la bascule de disponibilité. On copie data-inventory-level (pour la couleur via [data-inventory-level])
     * et l'innerHTML de .product-inventory__text (l'urgence contient du HTML <p>).
     */
    syncInventory() {
      if (!this.stickyInventory) return;
      const apply = () => {
        const inv = this.productSection.querySelector('.product-inventory');
        const text = inv && !inv.hidden ? inv.querySelector('.product-inventory__text') : null;
        if (text) {
          const level = inv.getAttribute('data-inventory-level');
          if (level) {
            this.stickyInventory.setAttribute('data-inventory-level', level);
          } else {
            this.stickyInventory.removeAttribute('data-inventory-level');
          }
          this.stickyInventory.innerHTML = text.innerHTML;
        } else {
          this.stickyInventory.removeAttribute('data-inventory-level');
          this.stickyInventory.innerHTML = '';
        }
      };
      apply();
      if (!this.inventoryObserver) {
        const inv = this.productSection.querySelector('.product-inventory');
        if (inv) {
          this.inventoryObserver = new MutationObserver(apply);
          this.inventoryObserver.observe(inv, {
            attributes: true,
            attributeFilter: ['data-inventory-level', 'hidden'],
            childList: true,
            subtree: true,
            characterData: true
          });
        }
      }
    }

    /**
     * Recale la quantité sticky sur la quantité principale (re-rendue/réinitialisée au changement de variante).
     */
    syncQtyFromMain() {
      if (!this.stickyQty) return;
      requestAnimationFrame(() => {
        const mainQty = this.productSection.querySelector('.js-product-form-main [name="quantity"]');
        if (mainQty && this.stickyQty.value !== mainQty.value) {
          this.stickyQty.value = mainQty.value;
        }
      });
    }

    /**
     * Ajout direct : recopie la quantité puis soumet le formulaire principal (AJAX existant).
     */
    addToCart() {
      const mainForm = this.productSection.querySelector('.js-product-form-main');
      if (!mainForm) {
        window.location.hash = this.variantsAnchor;
        return;
      }

      if (this.stickyQty) {
        const qty = parseInt(this.stickyQty.value, 10) || 1;
        const mainQty = mainForm.querySelector('[name="quantity"]');
        if (mainQty) {
          mainQty.value = qty;
          mainQty.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      const mainBtn = mainForm.querySelector('[name="add"]');
      if (typeof mainForm.requestSubmit === 'function') {
        mainForm.requestSubmit(mainBtn || undefined);
      } else if (mainBtn) {
        mainBtn.click();
      }
    }

    /**
     * Watches for a scroll to the bottom of the page
     */
    static handleScroll() {
      document.body.classList.toggle(
        'scrolled-to-bottom',
        window.scrollY + window.innerHeight + 100 > document.body.scrollHeight
      );
    }
  }

  customElements.define('sticky-atc-panel', StickyAtcPanel);
}
